<template>
  <div class="promociones-v3">

    <!-- ── Top bar ── -->
    <div class="topbar d-flex align-center px-4 py-2" style="gap:8px; flex-shrink:0;">
      <v-btn small @click="$router.back()" color="info" tile outlined>
        <v-icon left>mdi-arrow-left-bold</v-icon> Volver Atrás
      </v-btn>
      <v-spacer />
      <v-text-field
        v-model="search"
        dense outlined hide-details clearable
        placeholder="Buscar campaña..."
        prepend-inner-icon="mdi-magnify"
        style="max-width:260px;"
      />
      <v-btn small outlined :loading="loading" @click="getpromos" title="Actualizar listado">
        <v-icon small>mdi-refresh</v-icon>
      </v-btn>
      <v-badge :content="String(activeFilterCount)" :value="activeFilterCount > 0" color="error" overlap>
        <v-btn small outlined :color="showFilters ? 'primary' : ''" @click="showFilters = !showFilters">
          <v-icon left small>mdi-filter-variant</v-icon>
          Filtros
          <v-icon right x-small>{{ showFilters ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
      </v-badge>
    </div>

    <!-- ── Filter panel ── -->
    <v-expand-transition>
      <div v-show="showFilters" class="filter-panel px-4 py-3">
        <div class="d-flex flex-wrap align-start" style="gap:20px;">

          <!-- Estado -->
          <div class="filter-group">
            <div class="caption font-weight-bold grey--text text--darken-1 mb-2">
              <v-icon x-small class="mr-1">mdi-tag-outline</v-icon>Estado
            </div>
            <div class="d-flex flex-wrap" style="gap:6px;">
              <v-chip
                v-for="s in statusOptions" :key="s.value"
                small outlined
                :color="filterStatus.includes(s.value) ? s.activeColor : 'grey darken-1'"
                :class="{ 'font-weight-bold': filterStatus.includes(s.value) }"
                @click="toggleFilter('filterStatus', s.value)"
                style="cursor:pointer;"
              >
                <span class="filter-dot mr-1" :style="{ background: s.dotColor }"></span>
                {{ s.label }}
              </v-chip>
            </div>
          </div>

          <v-divider vertical />

          <!-- Plan de pago -->
          <div class="filter-group">
            <div class="caption font-weight-bold grey--text text--darken-1 mb-2">
              <v-icon x-small class="mr-1">mdi-credit-card-outline</v-icon>Plan de pago
            </div>
            <div class="d-flex flex-wrap" style="gap:6px;">
              <v-chip
                v-for="p in planOptions" :key="p.value"
                small outlined
                :color="filterPlan.includes(p.value) ? 'primary' : 'grey darken-1'"
                :class="{ 'font-weight-bold': filterPlan.includes(p.value) }"
                @click="toggleFilter('filterPlan', p.value)"
                style="cursor:pointer;"
              >{{ p.label }}</v-chip>
            </div>
          </div>

          <v-divider vertical />

          <!-- Modalidad -->
          <div class="filter-group">
            <div class="caption font-weight-bold grey--text text--darken-1 mb-2">
              <v-icon x-small class="mr-1">mdi-school-outline</v-icon>Modalidad
            </div>
            <div class="d-flex flex-wrap" style="gap:6px;">
              <v-chip
                v-for="m in modalidadOptions" :key="m.value"
                small outlined
                :color="filterModalidad.includes(m.value) ? 'primary' : 'grey darken-1'"
                :class="{ 'font-weight-bold': filterModalidad.includes(m.value) }"
                @click="toggleFilter('filterModalidad', m.value)"
                style="cursor:pointer;"
              >{{ m.label }}</v-chip>
            </div>
          </div>

          <v-divider vertical />

          <!-- Tipo de pago -->
          <div class="filter-group">
            <div class="caption font-weight-bold grey--text text--darken-1 mb-2">
              <v-icon x-small class="mr-1">mdi-cash-multiple</v-icon>Tipo de pago
            </div>
            <div class="d-flex flex-wrap" style="gap:6px;">
              <v-chip
                v-for="t in tipoPagoOptions" :key="t.value"
                small outlined
                :color="filterTipoPago.includes(t.value) ? 'deep-purple' : 'grey darken-1'"
                :class="{ 'font-weight-bold': filterTipoPago.includes(t.value) }"
                @click="toggleFilter('filterTipoPago', t.value)"
                style="cursor:pointer;"
              >{{ t.label }}</v-chip>
            </div>
          </div>

          <v-spacer />
          <div class="align-self-center">
            <v-btn x-small text color="error" :disabled="activeFilterCount === 0" @click="clearFilters">
              <v-icon x-small left>mdi-close-circle</v-icon> Limpiar
            </v-btn>
          </div>
        </div>
      </div>
    </v-expand-transition>

    <!-- ── Active filter chips (when panel is closed) ── -->
    <div v-if="activeFilterCount > 0 && !showFilters" class="px-4 pb-1 d-flex align-center flex-wrap" style="gap:4px; flex-shrink:0;">
      <span class="caption grey--text mr-1">Activos:</span>
      <v-chip
        v-for="s in filterStatus" :key="'fs-'+s"
        x-small close :color="statusConfig[s].chipColor" :dark="statusConfig[s].chipDark"
        @click:close="toggleFilter('filterStatus', s)"
      >{{ statusConfig[s].label }}</v-chip>
      <v-chip
        v-for="p in filterPlan" :key="'fp-'+p"
        x-small close color="primary" outlined
        @click:close="toggleFilter('filterPlan', p)"
      >{{ planLabel(p) }}</v-chip>
      <v-chip
        v-for="m in filterModalidad" :key="'fm-'+m"
        x-small close color="primary" outlined
        @click:close="toggleFilter('filterModalidad', m)"
      >{{ modalidadLabel(m) }}</v-chip>
      <v-chip
        v-for="t in filterTipoPago" :key="'ft-'+t"
        x-small close color="deep-purple" dark
        @click:close="toggleFilter('filterTipoPago', t)"
      >{{ tipoPagoLabel(t) }}</v-chip>
      <v-chip v-if="search" x-small close color="grey lighten-2" @click:close="search = ''">
        "{{ search }}"
      </v-chip>
    </div>

    <v-alert v-if="apiError" type="warning" dense text class="mx-4 mb-0">
      Sin conexión a la API — mostrando datos de ejemplo.
    </v-alert>
    <v-progress-linear v-if="loading" indeterminate color="primary" />

    <div class="layout-wrapper">

      <!-- ── LEFT: Calendar ── -->
      <div class="cal-section">

        <!-- Month navigation -->
        <div class="d-flex align-center mb-2">
          <v-btn icon small @click="prevMonth"><v-icon>mdi-chevron-left</v-icon></v-btn>
          <span class="text-h6 font-weight-bold mx-2">{{ mesLabel }}</span>
          <v-btn icon small @click="nextMonth"><v-icon>mdi-chevron-right</v-icon></v-btn>
          <v-spacer />
          <span v-if="activeFilterCount > 0" class="caption grey--text mr-3">
            <v-icon x-small color="primary" class="mr-1">mdi-filter</v-icon>
            {{ filteredPromociones.length }} de {{ promociones.length }}
          </span>
          <v-btn x-small text color="primary" @click="irHoy">Hoy</v-btn>
        </div>

        <!-- Calendar grid -->
        <div class="cal-grid elevation-1">
          <div v-for="d in diasSemana" :key="d" class="cal-header">{{ d }}</div>
          <div v-for="n in primerDiaSemana" :key="'e'+n" class="cal-cell cal-empty"></div>
          <div
            v-for="dia in diasDelMes"
            :key="'d'+dia"
            class="cal-cell"
            :class="{ 'cal-today': esHoy(dia), 'cal-selected': diaSeleccionado === dia }"
            @click="verDia(dia)"
          >
            <div class="cal-day-row">
              <span class="cal-day-num" :class="{ 'cal-day-today': esHoy(dia) }">{{ dia }}</span>
              <span v-if="promocionesDelDia(dia).length > 0" class="cal-day-count caption">
                {{ promocionesDelDia(dia).length }}
              </span>
            </div>
            <div
              v-for="promo in promocionesDelDia(dia).slice(0, 4)"
              :key="promo.id"
              class="cal-event"
              :class="'cal-event--' + getStatus(promo)"
              :title="promo.nombre"
            >
              <span class="cal-event-dot"></span>
              <span class="cal-event-name">{{ promo.nombre }}</span>
            </div>
            <div v-if="promocionesDelDia(dia).length > 4" class="cal-more">
              +{{ promocionesDelDia(dia).length - 4 }} más
            </div>
            <div class="cal-bars">
              <div v-if="contarStatus(dia,'vigente')>0"   class="cal-bar cal-bar--vigente"   :style="{ flex: contarStatus(dia,'vigente') }"></div>
              <div v-if="contarStatus(dia,'pendiente')>0" class="cal-bar cal-bar--pendiente" :style="{ flex: contarStatus(dia,'pendiente') }"></div>
              <div v-if="contarStatus(dia,'expirado')>0"  class="cal-bar cal-bar--expirado"  :style="{ flex: contarStatus(dia,'expirado') }"></div>
            </div>
          </div>
        </div>

        <!-- Bottom legend -->
        <div class="d-flex align-center justify-space-between mt-3 px-1">
          <div class="d-flex" style="gap:16px;">
            <div class="d-flex align-center" style="gap:5px;">
              <span class="legend-dot legend-dot--vigente"></span>
              <span class="caption grey--text text--darken-1">Vigente</span>
            </div>
            <div class="d-flex align-center" style="gap:5px;">
              <span class="legend-dot legend-dot--pendiente"></span>
              <span class="caption grey--text text--darken-1">Pendiente</span>
            </div>
            <div class="d-flex align-center" style="gap:5px;">
              <span class="legend-dot legend-dot--expirado"></span>
              <span class="caption grey--text text--darken-1">Expirado</span>
            </div>
          </div>
          <span v-if="activeFilterCount > 0" class="caption grey--text">
            Mostrando {{ filteredPromociones.length }} de {{ promociones.length }} campañas
          </span>
        </div>
      </div>

      <!-- ── RIGHT: Sidebar ── -->
      <div class="sidebar-panel">

        <!-- Header -->
        <div class="sidebar-header">
          <span class="subtitle-1 font-weight-bold">Campañas del mes</span>
          <v-btn
            small color="primary" depressed @click="abrirDialogNueva"
            v-if="$store.state.CARGO.id == 1 || $store.state.CARGO.id == 14"
          >
            <v-icon left small>mdi-plus</v-icon> Nueva
          </v-btn>
        </div>

        <v-divider />

        <!-- Stats -->
        <div class="sidebar-stats">
          <div class="sidebar-stat-col">
            <div class="text-h6 font-weight-bold">{{ stats.total }}</div>
            <div class="caption grey--text">Total</div>
          </div>
          <v-divider vertical />
          <div class="sidebar-stat-col">
            <div class="text-h6 font-weight-bold green--text text--darken-2">{{ stats.vigentes }}</div>
            <div class="caption grey--text">Vigentes</div>
          </div>
          <v-divider vertical />
          <div class="sidebar-stat-col">
            <div class="text-h6 font-weight-bold orange--text text--darken-2">{{ stats.pendientes }}</div>
            <div class="caption grey--text">Pendientes</div>
          </div>
          <v-divider vertical />
          <div class="sidebar-stat-col">
            <div class="text-h6 font-weight-bold grey--text">{{ stats.expirados }}</div>
            <div class="caption grey--text">Expiradas</div>
          </div>
        </div>

        <v-divider />

        <!-- Tabs + scrollable content -->
        <div class="sidebar-scroll">
          <div class="tab-bar">
            <button class="tab-btn" :class="{ 'tab-btn--active': tab === 0 }" @click="tab = 0">
              <v-icon small class="mr-1">mdi-format-list-bulleted</v-icon> Leyenda
            </button>
            <button class="tab-btn" :class="{ 'tab-btn--active': tab === 1 }" @click="tab = 1">
              <v-icon small class="mr-1">mdi-calendar-today</v-icon> Ver día
            </button>
            <button class="tab-btn tab-btn--purple" :class="{ 'tab-btn--active-purple': tab === 2 }" @click="tab = 2">
              <v-icon small class="mr-1">mdi-bookmark-multiple-outline</v-icon> Plantillas
              <v-chip v-if="templates.length > 0" x-small color="deep-purple" dark class="ml-1" style="height:16px;">
                {{ templates.length }}
              </v-chip>
            </button>
          </div>
          <v-divider />

          <!-- ─ Panel 0: Leyenda ─ -->
          <div v-show="tab === 0">
            <div v-if="promocionesDelMes.length === 0" class="pa-6 text-center">
              <v-icon large color="grey lighten-1">mdi-tag-off-outline</v-icon>
              <div class="caption grey--text mt-2">
                {{ activeFilterCount > 0 ? 'Sin resultados para los filtros aplicados.' : 'Sin campañas para este mes.' }}
              </div>
            </div>

            <div v-for="grupo in gruposStatus" :key="grupo.status">
              <v-subheader
                class="px-3 caption font-weight-bold"
                :class="grupo.colorClass"
                style="height:32px;"
              >
                <v-icon x-small left :color="grupo.iconColor">mdi-circle</v-icon>
                {{ grupo.label.toUpperCase() }}
                <v-spacer />
                <v-chip x-small color="grey lighten-3" class="font-weight-bold">{{ grupo.promos.length }}</v-chip>
              </v-subheader>

              <v-list dense class="py-0">
                <v-list-item
                  v-for="promo in grupo.promos"
                  :key="promo.id"
                  class="promo-list-item"
                  :class="'promo-list-item--' + grupo.status"
                >
                  <v-list-item-content class="py-2">
                    <v-list-item-title class="caption font-weight-bold" style="white-space:normal;line-height:1.3;">
                      {{ promo.nombre }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="caption mt-1">
                      <v-icon x-small class="mr-1">mdi-calendar-range</v-icon>
                      {{ formatRangoFecha(promo) }}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-if="promo.plan_pago || promo.modalidad" class="caption mt-1">
                      <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
                      <span v-if="promo.plan_pago">{{ planLabel(promo.plan_pago) }}</span>
                      <span v-if="promo.plan_pago && promo.modalidad"> · </span>
                      <span v-if="promo.modalidad">{{ modalidadLabel(promo.modalidad) }}</span>
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-if="promo.aprobado_por" class="caption mt-1 green--text text--darken-2">
                      <v-icon x-small color="green darken-2">mdi-check-circle</v-icon>
                      {{ promo.aprobado_por }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action class="my-0 ml-1 align-self-center" style="min-width:0;">
                    <div class="d-flex flex-column align-center" style="gap:4px;">
                      <div class="d-flex" style="gap:3px;">
                        <v-chip x-small :color="grupo.chipColor" :dark="grupo.chipDark">{{ grupo.chipLabel }}</v-chip>
                        <v-chip x-small :color="promoVersion(promo) === 'v3' ? 'blue darken-3' : 'grey darken-1'" dark>{{ promoVersion(promo) }}</v-chip>
                      </div>
                      <div class="d-flex" style="gap:2px;">
                        <v-btn icon x-small @click.stop="verDetalle(promo)" title="Ver detalle">
                          <v-icon x-small color="grey darken-1">mdi-eye-outline</v-icon>
                        </v-btn>
                        <v-btn v-if="promo.version_promocion === 'v3'" icon x-small @click.stop="$router.push(`/promociones-v3/${promo.id}/editar`)" title="Editar">
                          <v-icon x-small color="orange darken-2">mdi-pencil-outline</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              <v-divider />
            </div>
          </div>

          <!-- ─ Panel 1: Ver día ─ -->
          <div v-show="tab === 1">
            <div v-if="!diaSeleccionado" class="pa-8 text-center">
              <v-icon x-large color="grey lighten-2">mdi-cursor-default-click-outline</v-icon>
              <div class="body-2 grey--text mt-3">Haz clic en un día del calendario</div>
            </div>
            <template v-if="diaSeleccionado">
              <v-sheet color="primary" dark class="pa-3 d-flex align-center">
                <v-icon small class="mr-2">mdi-calendar</v-icon>
                <span class="subtitle-2 font-weight-bold">{{ diaLabelSeleccionado }}</span>
                <v-spacer />
                <v-chip small color="white" light>{{ promosDelDiaSeleccionado.length }}</v-chip>
              </v-sheet>

              <div v-if="promosDelDiaSeleccionado.length === 0" class="pa-6 text-center">
                <v-icon large color="grey lighten-2">mdi-tag-off-outline</v-icon>
                <div class="caption grey--text mt-2">Sin promociones este día.</div>
              </div>

              <v-list dense class="py-0">
                <v-list-item
                  v-for="promo in promosDelDiaSeleccionado"
                  :key="promo.id"
                  class="promo-list-item"
                  :class="'promo-list-item--' + getStatus(promo)"
                >
                  <v-list-item-content class="py-2">
                    <v-list-item-title class="caption font-weight-bold" style="white-space:normal;line-height:1.3;">
                      {{ promo.nombre }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="caption mt-1">
                      <v-icon x-small class="mr-1">mdi-calendar-range</v-icon>
                      {{ formatRangoFecha(promo) }}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-if="promo.plan_pago || promo.modalidad" class="caption mt-1">
                      <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
                      <span v-if="promo.plan_pago">{{ planLabel(promo.plan_pago) }}</span>
                      <span v-if="promo.plan_pago && promo.modalidad"> · </span>
                      <span v-if="promo.modalidad">{{ modalidadLabel(promo.modalidad) }}</span>
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-if="promo.aprobado_por" class="caption mt-1 green--text text--darken-2">
                      <v-icon x-small color="green darken-2">mdi-check-circle</v-icon>
                      {{ promo.aprobado_por }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action class="my-0 ml-1 align-self-center" style="min-width:0;">
                    <div class="d-flex flex-column align-center" style="gap:4px;">
                      <div class="d-flex" style="gap:3px;">
                        <v-chip x-small :color="statusConfig[getStatus(promo)].chipColor" :dark="statusConfig[getStatus(promo)].chipDark">
                          {{ statusConfig[getStatus(promo)].label }}
                        </v-chip>
                        <v-chip x-small :color="promoVersion(promo) === 'v3' ? 'blue darken-3' : 'grey darken-1'" dark>{{ promoVersion(promo) }}</v-chip>
                      </div>
                      <div class="d-flex" style="gap:2px;">
                        <v-btn icon x-small @click.stop="verDetalle(promo)" title="Ver detalle">
                          <v-icon x-small color="grey darken-1">mdi-eye-outline</v-icon>
                        </v-btn>
                        <v-btn v-if="promo.version_promocion === 'v3'" icon x-small @click.stop="$router.push(`/promociones-v3/${promo.id}/editar`)" title="Editar">
                          <v-icon x-small color="orange darken-2">mdi-pencil-outline</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </template>
          </div>

          <!-- ─ Panel 2: Plantillas ─ -->
          <div v-show="tab === 2">
            <!-- Loading -->
            <div v-if="loadingTemplates" class="pa-6 text-center">
              <v-progress-circular indeterminate color="deep-purple" size="28" />
              <div class="caption grey--text mt-2">Cargando plantillas...</div>
            </div>

            <!-- Vacío -->
            <div v-else-if="templates.length === 0" class="pa-6 text-center">
              <v-icon large color="grey lighten-2">mdi-bookmark-off-outline</v-icon>
              <div class="caption grey--text mt-2">Sin plantillas disponibles</div>
            </div>

            <!-- Lista de plantillas -->
            <v-list v-else dense class="py-0">
              <v-list-item
                v-for="t in templates"
                :key="t.id"
                class="tpl-sidebar-item"
              >
                <v-list-item-content class="py-2">
                  <v-list-item-title class="caption font-weight-bold" style="white-space:normal;line-height:1.3;">
                    <v-icon x-small color="deep-purple" class="mr-1">mdi-bookmark-outline</v-icon>
                    {{ t.nombre }}
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="t._unidad" class="caption mt-1">
                    <v-icon x-small class="mr-1">mdi-domain</v-icon>{{ t._unidad }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="caption mt-1">
                    <v-icon x-small class="mr-1">mdi-format-list-bulleted</v-icon>
                    {{ t.cantidad_reglas != null ? t.cantidad_reglas : (t.reglas ? t.reglas.length : 0) }}
                    regla{{ (t.cantidad_reglas || (t.reglas && t.reglas.length) || 0) !== 1 ? 's' : '' }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="my-0 ml-1 align-self-center" style="min-width:0;">
                  <div class="d-flex flex-column align-center" style="gap:4px;">
                    <v-btn icon x-small @click.stop="$router.push(`/promociones-v3/${t.id}`)" title="Ver detalle">
                      <v-icon x-small color="deep-purple">mdi-eye-outline</v-icon>
                    </v-btn>
                    <v-btn icon x-small @click.stop="crearDesdeTemplate(t)" title="Usar como base">
                      <v-icon x-small color="green darken-1">mdi-plus-circle-outline</v-icon>
                    </v-btn>
                    <v-btn icon x-small @click.stop="confirmarEliminarTemplate(t)" title="Eliminar plantilla">
                      <v-icon x-small color="red darken-1">mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>

        </div><!-- /sidebar-scroll -->
      </div><!-- /sidebar-panel -->

    </div><!-- /layout-wrapper -->

    <ViewPromo ref="viewmodal" />

    <!-- ── Dialog Eliminar Plantilla ── -->
    <v-dialog v-model="dialogEliminar" max-width="420" persistent>
      <v-card>
        <v-card-title class="py-4 px-5">
          <v-icon color="red darken-1" class="mr-2">mdi-delete-alert-outline</v-icon>
          <span class="subtitle-1 font-weight-bold">Eliminar plantilla</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="py-4 px-5">
          <p class="body-2 mb-1">¿Estás seguro de que deseas eliminar la plantilla:</p>
          <p class="body-2 font-weight-bold" v-if="templateAEliminar">
            "{{ templateAEliminar.nombre }}"
          </p>
          <p class="caption grey--text mb-0">Esta acción no se puede deshacer.</p>
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-5 py-3">
          <v-spacer />
          <v-btn text small @click="dialogEliminar = false" :disabled="eliminandoTemplate">Cancelar</v-btn>
          <v-btn color="red darken-1" dark small :loading="eliminandoTemplate" @click="doEliminarTemplate">
            <v-icon left small>mdi-delete</v-icon>Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Dialog Nueva Campaña ── -->
    <v-dialog v-model="dialogNueva" width="680" @click:outside="dialogNueva = false">
      <v-card>
        <v-card-title class="py-4 px-6">
          <v-icon color="primary" class="mr-2">mdi-tag-plus-outline</v-icon>
          <span class="subtitle-1 font-weight-bold">Crear Nueva Campaña</span>
          <v-spacer />
          <v-btn icon small @click="dialogNueva = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-6">
          <p class="body-2 grey--text text--darken-1 mb-4">¿Cómo deseas comenzar tu nueva campaña?</p>

          <!-- Loading -->
          <div v-if="loadingTemplates" class="text-center py-4">
            <v-progress-circular indeterminate color="primary" size="28" />
            <div class="caption grey--text mt-2">Cargando plantillas...</div>
          </div>

          <div v-else class="d-flex flex-wrap" style="gap:16px;">

            <!-- Desde cero -->
            <div class="tpl-card tpl-card--blank" @click="crearDesdeCero">
              <div class="tpl-card-icon">
                <v-icon size="34" color="grey darken-2">mdi-plus-circle-outline</v-icon>
              </div>
              <div class="tpl-card-title">Crear desde cero</div>
              <div class="tpl-card-desc">Configura todas las reglas y conceptos manualmente.</div>
            </div>

            <!-- Plantillas del API -->
            <div
              v-for="t in templates"
              :key="t.id"
              class="tpl-card"
              @click="crearDesdeTemplate(t)"
            >
              <div class="d-flex align-center mb-2" style="gap:8px;">
                <v-icon size="22" color="primary">mdi-bookmark-outline</v-icon>
                <v-chip x-small color="blue darken-4" dark>Plantilla</v-chip>
              </div>

              <div class="tpl-card-title">{{ t.nombre }}</div>

              <!-- Unidad de negocio -->
              <div v-if="t._unidad" class="d-flex align-center mt-1 mb-1">
                <v-icon x-small color="grey" class="mr-1">mdi-domain</v-icon>
                <span class="caption grey--text text--darken-1">{{ t._unidad }}</span>
              </div>

              <!-- Cantidad de reglas -->
              <div class="d-flex align-center justify-space-between mt-auto pt-3">
                <v-chip x-small color="primary" outlined>
                  <v-icon x-small left>mdi-format-list-bulleted</v-icon>
                  {{ t.cantidad_reglas != null ? t.cantidad_reglas : (t.reglas ? t.reglas.length : 0) }}
                  Regla{{ (t.cantidad_reglas || (t.reglas && t.reglas.length) || 0) !== 1 ? 's' : '' }}
                </v-chip>
                <div class="d-flex align-center" style="gap:6px;">
                  <v-btn icon x-small @click.stop="confirmarEliminarTemplate(t)" title="Eliminar plantilla">
                    <v-icon x-small color="red darken-1">mdi-delete-outline</v-icon>
                  </v-btn>
                  <span class="caption primary--text font-weight-medium">Usar →</span>
                </div>
              </div>
            </div>

            <!-- Sin plantillas -->
            <div v-if="templates.length === 0" class="tpl-empty">
              <v-icon color="grey lighten-2" size="32">mdi-bookmark-off-outline</v-icon>
              <div class="caption grey--text mt-2">Sin plantillas guardadas</div>
            </div>

          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import ViewPromo from '@/views/promociones/promo-view.vue';
import xaxios from '@/xaxios';
import gql from 'graphql-tag';

export default {
  name: 'PromocionesV3',
  components: { ViewPromo },
  data() {
    return {
      loading: false,
      apiError: false,
      tab: 0,
      dialogNueva: false,
      templates: [],
      loadingTemplates: false,
      dialogEliminar: false,
      templateAEliminar: null,
      eliminandoTemplate: false,
      unidades: [],
      currentDate: new Date(),
      diaSeleccionado: null,
      promociones: [],
      diasSemana: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'],
      // Filters
      search: '',
      showFilters: false,
      filterStatus: [],
      filterPlan: [],
      filterModalidad: [],
      filterTipoPago: [],
      // Options
      statusOptions: [
        { value: 'vigente',   label: 'Vigente',   dotColor: '#4CAF50', activeColor: 'green darken-1' },
        { value: 'pendiente', label: 'Pendiente', dotColor: '#FF9800', activeColor: 'orange darken-1' },
        { value: 'expirado',  label: 'Expirado',  dotColor: '#9E9E9E', activeColor: 'grey darken-1' },
      ],
      planOptions: [
        { value: 'credito', label: 'Crédito' },
        { value: 'contado', label: 'Contado' },
      ],
      modalidadOptions: [
        { value: 'teorico',          label: 'Teórico' },
        { value: 'practico',         label: 'Práctico' },
        { value: 'teorico_practico', label: 'Teórico-Práctico' },
      ],
      tipoPagoOptions: [
        { value: 'promocional', label: 'Promocional' },
        { value: 'ancla',       label: 'Ancla' },
        { value: 'normal',      label: 'Normal' },
      ],
      // Status config (colors/labels)
      statusConfig: {
        vigente:  { label: 'Vigente',   chipColor: 'green darken-1',  chipDark: true,  colorClass: 'green--text text--darken-3',  iconColor: 'green darken-1' },
        pendiente:{ label: 'Pendiente', chipColor: 'orange darken-1', chipDark: true,  colorClass: 'orange--text text--darken-3', iconColor: 'orange darken-1' },
        expirado: { label: 'Expirado',  chipColor: 'grey lighten-1',  chipDark: false, colorClass: 'grey--text',                  iconColor: 'grey' },
      },
    };
  },
  computed: {
    mesLabel() {
      const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio',
        'Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
      return `${meses[this.currentDate.getMonth()]} ${this.currentDate.getFullYear()}`;
    },
    diasDelMes() {
      const { y, m } = this.ym;
      return Array.from({ length: new Date(y, m + 1, 0).getDate() }, (_, i) => i + 1);
    },
    primerDiaSemana() {
      const { y, m } = this.ym;
      return (new Date(y, m, 1).getDay() + 6) % 7;
    },
    ym() {
      return { y: this.currentDate.getFullYear(), m: this.currentDate.getMonth() };
    },
    primerDia() {
      const { y, m } = this.ym;
      return new Date(y, m, 1);
    },
    ultimoDia() {
      const { y, m } = this.ym;
      return new Date(y, m + 1, 0);
    },
    activeFilterCount() {
      return this.filterStatus.length + this.filterPlan.length +
        this.filterModalidad.length + this.filterTipoPago.length +
        (this.search ? 1 : 0);
    },
    filteredPromociones() {
      const q = this.search ? this.search.toLowerCase() : '';
      return this.promociones.filter(p => {
        if (q && !p.nombre.toLowerCase().includes(q)) return false;
        if (this.filterStatus.length && !this.filterStatus.includes(this.getStatus(p))) return false;
        if (this.filterPlan.length && !this.filterPlan.includes(p.plan_pago)) return false;
        if (this.filterModalidad.length && !this.filterModalidad.includes(p.modalidad)) return false;
        if (this.filterTipoPago.length && !this.filterTipoPago.includes(p.tipo_pago)) return false;
        return true;
      });
    },
    promocionesDelMes() {
      return this.filteredPromociones.filter(p => {
        const rangos = this.getRangos(p);
        return rangos.some(r => r && r.fin >= this.primerDia && r.inicio <= this.ultimoDia);
      });
    },
    gruposStatus() {
      const grupos = { vigente: [], pendiente: [], expirado: [] };
      this.promocionesDelMes.forEach(p => { grupos[this.getStatus(p)].push(p); });
      return [
        { status: 'vigente',   label: 'Vigentes',   colorClass: 'green--text text--darken-3',  iconColor: 'green darken-1',  chipColor: 'green darken-1',  chipDark: true,  chipLabel: 'Vigente',   promos: grupos.vigente },
        { status: 'pendiente', label: 'Pendientes', colorClass: 'orange--text text--darken-3', iconColor: 'orange darken-1', chipColor: 'orange darken-1', chipDark: true,  chipLabel: 'Pendiente', promos: grupos.pendiente },
        { status: 'expirado',  label: 'Expiradas',  colorClass: 'grey--text',                  iconColor: 'grey',            chipColor: 'grey lighten-1',  chipDark: false, chipLabel: 'Expirado',  promos: grupos.expirado },
      ].filter(g => g.promos.length > 0);
    },
    stats() {
      const v = this.promocionesDelMes.filter(p => this.getStatus(p) === 'vigente').length;
      const p = this.promocionesDelMes.filter(p => this.getStatus(p) === 'pendiente').length;
      const e = this.promocionesDelMes.filter(p => this.getStatus(p) === 'expirado').length;
      return { total: this.promocionesDelMes.length, vigentes: v, pendientes: p, expirados: e };
    },
    promosDelDiaSeleccionado() {
      if (!this.diaSeleccionado) return [];
      return this.promocionesDelDia(this.diaSeleccionado);
    },
    diaLabelSeleccionado() {
      if (!this.diaSeleccionado) return '';
      const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio',
        'Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
      const { y, m } = this.ym;
      return `${this.diaSeleccionado} de ${meses[m]} ${y}`;
    },
  },
  methods: {
    promoVersion(promo) {
      return promo.version_promocion || 'v1';
    },
    getRangos(promo) {
      if (promo.fechas && promo.fechas.length > 0) {
        return promo.fechas
          .filter(f => f.fecha_inicio && f.fecha_fin)
          .map(f => ({ inicio: this.parseDate(f.fecha_inicio), fin: this.parseDate(f.fecha_fin) }));
      }
      if (promo.fecha_inicio && promo.fecha_fin) {
        return [{ inicio: this.parseDate(promo.fecha_inicio), fin: this.parseDate(promo.fecha_fin) }];
      }
      return [];
    },
    parseDate(str) {
      if (!str) return null;
      const [y, m, d] = String(str).split('-').map(Number);
      return new Date(y, m - 1, d);
    },
    getStatus(promo) {
      if (!promo.aprobado_por) return 'pendiente';
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const rangos = this.getRangos(promo);
      return rangos.some(r => r && today >= r.inicio && today <= r.fin) ? 'vigente' : 'expirado';
    },
    promocionesDelDia(dia) {
      const { y, m } = this.ym;
      const date = new Date(y, m, dia);
      return this.promocionesDelMes.filter(p =>
        this.getRangos(p).some(r => r && date >= r.inicio && date <= r.fin)
      );
    },
    contarStatus(dia, status) {
      return this.promocionesDelDia(dia).filter(p => this.getStatus(p) === status).length;
    },
    esHoy(dia) {
      const t = new Date();
      const { y, m } = this.ym;
      return t.getFullYear() === y && t.getMonth() === m && t.getDate() === dia;
    },
    formatRangoFecha(promo) {
      const rangos = this.getRangos(promo);
      if (!rangos.length) return '—';
      const r = rangos[0];
      if (!r) return '—';
      if (r.inicio <= this.primerDia && r.fin >= this.ultimoDia) return 'Todo el mes';
      const dS = r.inicio < this.primerDia ? 1 : r.inicio.getDate();
      const dE = r.fin > this.ultimoDia ? this.ultimoDia.getDate() : r.fin.getDate();
      return `Días ${dS}–${dE}`;
    },
    planLabel(val) {
      const map = { credito: 'Crédito', contado: 'Contado' };
      return map[val] || val || '—';
    },
    modalidadLabel(val) {
      const map = { teorico: 'Teórico', practico: 'Práctico', teorico_practico: 'Teórico-Práctico' };
      return map[val] || val || '—';
    },
    tipoPagoLabel(val) {
      const map = { promocional: 'Promocional', ancla: 'Ancla', normal: 'Normal' };
      return map[val] || val || '—';
    },
    toggleFilter(key, value) {
      const arr = this[key];
      const idx = arr.indexOf(value);
      if (idx === -1) arr.push(value);
      else arr.splice(idx, 1);
    },
    clearFilters() {
      this.filterStatus = [];
      this.filterPlan = [];
      this.filterModalidad = [];
      this.filterTipoPago = [];
      this.search = '';
    },
    verDia(dia) {
      this.diaSeleccionado = dia;
      this.tab = 1;
    },
    prevMonth() {
      const { y, m } = this.ym;
      this.currentDate = new Date(y, m - 1, 1);
      this.diaSeleccionado = null;
    },
    nextMonth() {
      const { y, m } = this.ym;
      this.currentDate = new Date(y, m + 1, 1);
      this.diaSeleccionado = null;
    },
    irHoy() {
      this.currentDate = new Date();
      this.diaSeleccionado = null;
    },
    verDetalle(promo) {
      if (promo.version_promocion === 'v3') {
        this.$router.push(`/promociones-v3/${promo.id}`);
      } else {
        this.$refs.viewmodal.openmodal(promo);
      }
    },
    async loadTemplates() {
      this.loadingTemplates = true;
      if (this.unidades.length === 0) await this.loadUnidades();
      try {
        const res = await xaxios.request({
          method: 'get',
          url: `${process.env.VUE_APP_API_REST}/api/v1/esam/promociones/templates`,
        });
        this.templates = (res.data.data || res.data || []).map(t => ({
          ...t,
          _source: 'api',
          _unidad: this.parseUnidadNombre(t.ambiente),
        }));
      } catch {
        try { this.templates = JSON.parse(localStorage.getItem('promo_v3_templates') || '[]'); } catch { this.templates = []; }
      } finally {
        this.loadingTemplates = false;
      }
    },
    async abrirDialogNueva() {
      this.dialogNueva = true;
      if (this.templates.length === 0) await this.loadTemplates();
    },
    async loadUnidades() {
      try {
        const res = await this.$apollo.query({
          query: gql`query { NegociosAll { id nombre } }`,
        });
        this.unidades = res.data.NegociosAll || [];
      } catch { /* ignore */ }
    },
    parseUnidadNombre(ambienteStr) {
      try {
        const a = typeof ambienteStr === 'string' ? JSON.parse(ambienteStr) : ambienteStr;
        if (a && a.detalle && a.detalle.length > 0) {
          const codigoId = a.detalle[0].codigo_id;
          const u = this.unidades.find(x => x.id === codigoId);
          return u ? u.nombre : (a.detalle[0].nombre || null);
        }
      } catch { /* ignore */ }
      return null;
    },
    crearDesdeCero() {
      localStorage.removeItem('promo_v3_template_pending');
      this.dialogNueva = false;
      this.$router.push('/promociones-v3/nueva');
    },
    crearDesdeTemplate(t) {
      if (t._source === 'api') {
        localStorage.setItem('promo_v3_template_pending', JSON.stringify({ source: 'api', id: t.id }));
      } else {
        localStorage.setItem('promo_v3_template_pending', JSON.stringify({
          source:            'local',
          descripcion:       t.descripcion       || '',
          unidad_negocio_id: t.unidad_negocio_id || null,
          reglas:            t.reglas            || [],
        }));
      }
      this.dialogNueva = false;
      this.$router.push('/promociones-v3/nueva');
    },
    eliminarTemplate(id) {
      this.templates = this.templates.filter(t => t.id !== id);
      localStorage.setItem('promo_v3_templates', JSON.stringify(this.templates.filter(t => t._source !== 'api')));
    },
    confirmarEliminarTemplate(t) {
      this.templateAEliminar = t;
      this.dialogEliminar = true;
    },
    async doEliminarTemplate() {
      if (!this.templateAEliminar) return;
      this.eliminandoTemplate = true;
      try {
        await xaxios.delete(
          `${process.env.VUE_APP_API_REST}/api/v1/esam/promociones/templates/${this.templateAEliminar.id}`
        );
        this.eliminarTemplate(this.templateAEliminar.id);
        this.dialogEliminar = false;
        this.templateAEliminar = null;
      } catch (e) {
        window.console.error('Error eliminando plantilla:', e);
      } finally {
        this.eliminandoTemplate = false;
      }
    },
    async getpromos() {
      this.loading = true;
      this.apiError = false;
      try {
        const { y, m } = this.ym;
        const config = {
          method: 'get',
          url: `${process.env.VUE_APP_API_REST}/api/v1/esam/promociones`,
          params: { mes: m + 1, anio: y, version_promocion: 'v3' },
        };
        const response = await xaxios.request(config);
        this.promociones = (response.data.data || []).reverse();
      } catch (e) {
        this.apiError = true;
        this.promociones = [];
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    currentDate() { this.getpromos(); },
  },
  mounted() {
    this.getpromos();
    this.loadTemplates();
  },
};
</script>

<style scoped>
/* ── Wrapper ─────────────────────────────────── */
.promociones-v3 {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}
.topbar { flex-shrink: 0; }

/* ── Filter panel ────────────────────────────── */
.filter-panel {
  flex-shrink: 0;
  background: #f8f9fa;
  border-top: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
}
.filter-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
  vertical-align: middle;
}

/* ── Layout ──────────────────────────────────── */
.layout-wrapper {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 0 12px 12px;
  gap: 12px;
}

/* ── Calendar section ────────────────────────── */
.cal-section {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── Grid ────────────────────────────────────── */
.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  border-radius: 6px;
  overflow: hidden;
  flex: 1;
  align-content: start;
}
.cal-header {
  background: #f5f5f5;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #616161;
  padding: 8px 4px;
  border-bottom: 2px solid #e0e0e0;
}
.cal-cell {
  border-right: 1px solid #eeeeee;
  border-bottom: 1px solid #eeeeee;
  padding: 4px 5px 5px;
  cursor: pointer;
  background: white;
  transition: background 0.1s;
  display: flex;
  flex-direction: column;
  min-height: 120px;
}
.cal-cell:hover   { background: #fafafa; }
.cal-empty        { background: #f9f9f9; cursor: default; min-height: 120px; }
.cal-today        { background: #fffde7; }
.cal-selected     { background: #e8eaf6 !important; }

/* ── Day number row ──────────────────────────── */
.cal-day-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3px;
}
.cal-day-num {
  font-size: 12px;
  font-weight: 500;
  color: #424242;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}
.cal-day-today {
  background: #3f51b5;
  color: white !important;
  font-weight: 700;
}
.cal-day-count {
  font-size: 10px;
  color: #bdbdbd;
  line-height: 1;
}

/* ── Events ──────────────────────────────────── */
.cal-event {
  display: flex;
  align-items: center;
  font-size: 11px;
  padding: 2px 5px;
  border-radius: 3px;
  margin-bottom: 2px;
  overflow: hidden;
  cursor: pointer;
  gap: 4px;
}
.cal-event-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.cal-event-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
  line-height: 1.4;
}
.cal-event--vigente              { background: #E8F5E9; color: #1B5E20; }
.cal-event--vigente .cal-event-dot   { background: #4CAF50; }
.cal-event--pendiente            { background: #FFF3E0; color: #BF360C; }
.cal-event--pendiente .cal-event-dot { background: #FF9800; }
.cal-event--expirado             { background: #F5F5F5; color: #757575; }
.cal-event--expirado .cal-event-dot  { background: #9E9E9E; }

.cal-more {
  font-size: 10px;
  color: #9e9e9e;
  padding-left: 3px;
  margin-top: 1px;
}

/* ── Status bars ─────────────────────────────── */
.cal-bars {
  display: flex;
  gap: 2px;
  margin-top: auto;
  padding-top: 4px;
}
.cal-bar           { height: 4px; border-radius: 2px; min-width: 4px; }
.cal-bar--vigente  { background: #4CAF50; }
.cal-bar--pendiente{ background: #FF9800; }
.cal-bar--expirado { background: #BDBDBD; }

/* ── Bottom legend ───────────────────────────── */
.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}
.legend-dot--vigente  { background: #4CAF50; }
.legend-dot--pendiente{ background: #FF9800; }
.legend-dot--expirado { background: #9E9E9E; }

/* ── Sidebar ─────────────────────────────────── */
.sidebar-panel {
  width: 320px;
  min-width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.12);
  border-radius: 4px;
  background: #fff;
}
.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  flex-shrink: 0;
}
.sidebar-stats {
  display: flex;
  align-items: center;
  text-align: center;
  flex-shrink: 0;
  padding: 8px 0;
}
.sidebar-stat-col { flex: 1; }
.sidebar-scroll {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

/* ── Tab bar ──────────────────────────────────── */
.tab-bar {
  display: flex;
  background: #f5f5f5;
  flex-shrink: 0;
}
.tab-btn {
  flex: 1;
  height: 40px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: #616161;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-bottom: 2px solid transparent;
  transition: color 0.2s, border-color 0.2s;
}
.tab-btn:hover { background: #eeeeee; }
.tab-btn--active {
  color: #1976d2;
  border-bottom-color: #1976d2;
  background: #fff;
}

/* ── Promo list items ────────────────────────── */
.promo-list-item { border-left: 3px solid transparent; }
.promo-list-item--vigente  { border-left-color: #388E3C; background: #F9FBE7; }
.promo-list-item--pendiente{ border-left-color: #F57C00; background: #FFF8F0; }
.promo-list-item--expirado { border-left-color: #BDBDBD; background: #FAFAFA; }

/* ── Template cards ──────────────────────────── */
.tpl-card {
  width: 190px;
  min-height: 130px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  padding: 18px 16px 14px;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  display: flex;
  flex-direction: column;
}
.tpl-card:hover {
  border-color: #1976d2;
  box-shadow: 0 2px 12px rgba(25,118,210,.13);
}
.tpl-card--blank {
  border-style: dashed;
  align-items: center;
  text-align: center;
}
.tpl-card--blank:hover { border-color: #757575; box-shadow: none; }
.tpl-card-icon  { margin-bottom: 10px; }
.tpl-card-title { font-size: 14px; font-weight: 700; color: #212121; margin-bottom: 2px; }
.tpl-card-desc  { font-size: 12px; color: #757575; }
.tpl-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #bdbdbd;
}

/* ── Tab plantillas ──────────────────────────── */
.tab-btn--purple { color: #6a1b9a; }
.tab-btn--active-purple {
  color: #6a1b9a !important;
  border-bottom-color: #6a1b9a !important;
  background: #fff !important;
}

/* ── Plantillas en sidebar ───────────────────── */
.tpl-sidebar-item {
  border-left: 3px solid #ce93d8;
  border-bottom: 1px solid #f3e5f5;
}
.tpl-sidebar-item:hover { background: #f9f0ff; }
</style>
